import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { apiUrl, apiFetch } from '../api';
import {
  setAccessToken,
  getRefreshToken,
  setRefreshToken,
  clearTokens,
  decodeJwtPayload,
  getAccessToken,
} from './tokenStore';

const AuthContext = createContext(undefined);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [initializing, setInitializing] = useState(true);

  const fetchProfile = useCallback(async () => {
    try {
      const data = await apiFetch('/api/me/');
      setUser({ username: data.username, role: data.role, id: data.id });
    } catch {
      const payload = decodeJwtPayload(getAccessToken());
      if (payload) {
        setUser({ username: payload.username, role: payload.role || 'viewer' });
      }
    }
  }, []);

  const login = useCallback(async (username, password) => {
    const res = await fetch(apiUrl('token/'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password }),
    });
    if (!res.ok) {
      if (res.status === 404) {
        throw new Error('Сервис авторизации недоступен. Проверьте, что бэкенд запущен.');
      }
      throw new Error('Неверный логин или пароль');
    }
    const data = await res.json();
    setAccessToken(data.access);
    setRefreshToken(data.refresh);
    await fetchProfile();
  }, [fetchProfile]);

  const logout = useCallback(() => {
    clearTokens();
    setUser(null);
  }, []);

  useEffect(() => {
    const refresh = getRefreshToken();
    if (!refresh) {
      setInitializing(false);
      return;
    }
    fetch(apiUrl('token/refresh/'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh }),
    })
      .then((res) => {
        if (!res.ok) throw new Error('refresh failed');
        return res.json();
      })
      .then(async (data) => {
        setAccessToken(data.access);
        await fetchProfile();
      })
      .catch(() => {
        clearTokens();
      })
      .finally(() => setInitializing(false));
  }, [fetchProfile]);

  useEffect(() => {
    const onForceLogout = () => setUser(null);
    window.addEventListener('auth:logout', onForceLogout);
    return () => window.removeEventListener('auth:logout', onForceLogout);
  }, []);

  return (
    <AuthContext.Provider
      value={{ user, isAuthenticated: !!user, login, logout, initializing }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth должен использоваться внутри <AuthProvider>');
  return ctx;
}
